<template>
    <modal :show="show" @update:show="handleShowChange" modal-classes="modal-lg amaze-push-modal">
        <h5 slot="header" class="modal-title">
            {{ $t("amaze_push.title") }}
        </h5>

        <div>
            <div class="row mb-3">
                <div class="col-md-12">
                    <label class="form-control-label">{{ $t("amaze_push.product") }}</label>
                    <p class="font-weight-bold mb-0">{{ productName }}</p>
                </div>
            </div>

            <!-- 1. platform. Unsupported platforms are LISTED and disabled with
                 a reason rather than hidden (spec 6B.8). -->
            <div class="row mb-3">
                <div class="col-md-12">
                    <label class="form-control-label">{{ $t("amaze_push.platform_label") }}</label>
                    <el-select v-model="platformId" class="w-100" :disabled="polling">
                        <el-option
                            v-for="p in platforms"
                            :key="p.id"
                            :label="$t(p.labelKey)"
                            :value="p.id"
                            :disabled="!p.enabled"
                        >
                            <span>{{ $t(p.labelKey) }}</span>
                            <span v-if="!p.enabled" class="text-muted float-right ml-3">
                                {{ $t(p.reason) }}
                            </span>
                        </el-option>
                    </el-select>
                </div>
            </div>

            <!-- rejects, TWICE, because the same list means two things.
                 A 422 has no payload: the list is what blocks the push.
                 A 200 has one: the payload goes WITHOUT these SKUs (§6B.3 rule
                 7), so the list is a skip notice and must not look fatal. -->
            <div class="row mb-3" v-if="blockingRejects.length">
                <div class="col-md-12">
                    <div class="alert alert-danger py-2 px-3">
                        <strong>{{ $t("amaze_push.rejected_title") }}</strong>
                        <ul class="mb-0 mt-2 pl-4">
                            <li v-for="(rj, idx) in blockingRejects" :key="idx">
                                {{ rejectText(rj) }}
                                <span v-if="!isProductLevel(rj)" class="text-muted">
                                    ({{ $t("amaze_push.col_sku_code") }}: {{ rj.sku_code || rj.sku_id }})
                                </span>
                                <span v-if="rejectDetail(rj)" class="text-muted">— {{ rejectDetail(rj) }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="row mb-3" v-if="skippedRejects.length">
                <div class="col-md-12">
                    <div class="alert alert-warning py-2 px-3">
                        <strong>{{ $t("amaze_push.skipped_title") }}</strong>
                        <ul class="mb-0 mt-2 pl-4">
                            <li v-for="(rj, idx) in skippedRejects" :key="idx">
                                {{ rejectText(rj) }}
                                <span v-if="!isProductLevel(rj)" class="text-muted">
                                    ({{ $t("amaze_push.col_sku_code") }}: {{ rj.sku_code || rj.sku_id }})
                                </span>
                                <span v-if="rejectDetail(rj)" class="text-muted">— {{ rejectDetail(rj) }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- other errors. category_not_mapped is the one an operator can
                 fix themselves, so it carries a link to the mapping page. -->
            <div class="row mb-3" v-if="errorKey">
                <div class="col-md-12">
                    <div class="alert alert-danger py-2 px-3 mb-0">
                        <i class="fa fa-exclamation-triangle mr-1"></i>
                        {{ $t(errorKey) }}
                        <a
                            href="#"
                            v-if="showMappingLink"
                            class="ml-2 font-weight-bold"
                            @click.prevent="openCategoryMap"
                        >
                            {{ $t("amaze_push.go_category_map") }}
                        </a>
                        <span v-if="errorDetail" class="d-block small">{{ errorDetail }}</span>
                    </div>
                </div>
            </div>

            <!-- 2. preview of the dry run -->
            <div v-if="preview">
                <hr />
                <div class="row mb-3">
                    <div class="col-md-5">
                        <label class="form-control-label">{{ $t("amaze_push.summary_name") }}</label>
                        <p class="font-weight-bold mb-0">{{ summary.name_th || "-" }}</p>
                    </div>
                    <div class="col-md-3">
                        <label class="form-control-label">{{ $t("amaze_push.summary_action") }}</label>
                        <p class="font-weight-bold mb-0">{{ $t(actionKey) }}</p>
                    </div>
                    <div class="col-md-2">
                        <label class="form-control-label">{{ $t("amaze_push.summary_images") }}</label>
                        <p class="font-weight-bold mb-0">{{ summary.image_count }}</p>
                    </div>
                    <div class="col-md-2">
                        <label class="form-control-label">{{ $t("amaze_push.summary_models") }}</label>
                        <p class="font-weight-bold mb-0">{{ summary.model_count }}</p>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-5">
                        <label class="form-control-label">{{ $t("amaze_push.summary_category") }}</label>
                        <p class="mb-0 small text-monospace">{{ summary.category_group_id || "-" }}</p>
                    </div>
                    <div class="col-md-4">
                        <label class="form-control-label">{{ $t("amaze_push.summary_brand") }}</label>
                        <p class="mb-0 small text-monospace">{{ summary.brand_id || "-" }}</p>
                    </div>
                    <div class="col-md-3">
                        <label class="form-control-label">{{ $t("amaze_push.summary_parcel") }}</label>
                        <p class="mb-0 small">
                            {{ summary.parcel_size.width }}×{{ summary.parcel_size.length }}×{{
                                summary.parcel_size.height
                            }}
                            · {{ summary.weight }}
                        </p>
                    </div>
                </div>

                <label class="form-control-label">
                    {{ $t("amaze_push.sku_table") }} ({{ skus.length }})
                </label>
                <el-table :data="skus" header-row-class-name="thead-light" max-height="300" size="mini">
                    <el-table-column :label="$t('amaze_push.col_sku_code')" min-width="160">
                        <template slot-scope="{ row: sku }">
                            {{ sku.sku_code }}
                            <!-- The code ACTUALLY sent, when it differs. This
                                 pair is the only thing that explains an order
                                 arriving under a code nobody recognises. -->
                            <span v-if="sku.code_differs" class="text-muted small d-block">
                                {{ $t("amaze_push.col_sent_code") }}: {{ sku.sent_code }}
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('amaze_push.col_action')" width="110" align="center">
                        <template slot-scope="{ row: sku }">
                            <el-tag size="mini" :type="actionTagType(sku.action)">
                                {{ $t(actionI18nKey(sku.action)) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('amaze_push.col_reason')" min-width="200">
                        <template slot-scope="{ row: sku }">
                            <span class="text-muted">{{ skuReason(sku) }}</span>
                        </template>
                    </el-table-column>
                </el-table>

                <div class="mt-3" v-if="warnings.length">
                    <div class="alert alert-warning py-2 px-3 mb-0">
                        <strong>{{ $t("amaze_push.warnings_title") }}</strong>
                        <span class="text-muted small ml-1" v-if="isDryRunPreview">
                            ({{ $t("amaze_push.warnings_dry_run") }})
                        </span>
                        <ul class="mb-0 mt-2 pl-4">
                            <li v-for="(w, idx) in warnings" :key="idx">
                                {{ warningText(w) }}
                                <span v-if="w.message" class="text-muted">— {{ w.message }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- 3. progress while the job runs -->
            <div class="row mt-3" v-if="jobId">
                <div class="col-md-12">
                    <div class="alert alert-info py-2 px-3 mb-0">
                        <div class="d-flex align-items-center mb-2">
                            <i class="fa fa-spinner fa-spin mr-2" v-if="polling"></i>
                            <strong>{{ $t("amaze_push.job_title") }} #{{ jobId }}</strong>
                            <span class="ml-2">{{ $t(stepKey) }}</span>
                        </div>
                        <div>
                            <el-tag
                                v-for="(s, idx) in stepLabels"
                                :key="s"
                                size="mini"
                                class="mr-1"
                                :type="idx <= stepIndex ? 'success' : 'info'"
                            >
                                {{ $t("amaze_push.step." + s) }}
                            </el-tag>
                        </div>
                        <el-progress
                            v-if="progressPercent > 0"
                            class="mt-2"
                            :percentage="progressPercent"
                        ></el-progress>
                    </div>
                </div>
            </div>

            <!-- 4. result -->
            <div class="row mt-3" v-if="finishedState">
                <div class="col-md-12">
                    <div
                        class="py-2 px-3 mb-0 alert"
                        :class="isFailed ? 'alert-danger' : 'alert-success'"
                    >
                        <i class="fa mr-1" :class="isFailed ? 'fa-times-circle' : 'fa-check-circle'"></i>
                        {{ $t(stateKey) }}
                        <span v-if="platformProductId" class="d-block small text-monospace">
                            {{ $t("amaze_push.platform_product_id") }}: {{ platformProductId }}
                        </span>
                        <span v-if="stateDetail" class="d-block small">{{ stateDetail }}</span>
                        <span v-if="lastError" class="d-block small">{{ lastError }}</span>
                        <a href="#" v-if="isFailed" class="d-block mt-1 font-weight-bold" @click.prevent="retry">
                            {{ $t("amaze_push.btn_retry") }}
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <template slot="footer">
            <button
                type="button"
                class="btn btn-md btn-primary"
                :disabled="loading || polling || !platformEnabled"
                @click="handleCheck()"
                v-if="!finishedState"
            >
                <i class="fa fa-spinner fa-spin mr-1" v-if="loading"></i>
                {{ $t("amaze_push.btn_check") }}
            </button>
            <button
                type="button"
                class="btn btn-md btn-success"
                :disabled="loading || polling || !canConfirm"
                @click="handleConfirm()"
                v-if="!finishedState"
            >
                <i class="fa fa-spinner fa-spin mr-1" v-if="loading"></i>
                {{ $t("amaze_push.btn_confirm") }}
            </button>
            <button type="button" class="btn btn-md btn-link ml-auto" @click="close()">
                {{ $t("close") }}
            </button>
        </template>
    </modal>
</template>

<script>
// #263 — "sync ไป platform" modal on /app-catalog/products/:id/edit.
// Spec: docs/specs/2026-09-22-shopee-master-product-sync-design.md §6B.8.
//
// THE TWO-STEP CONTRACT IS LOAD-BEARING and asserted by
// tests/unit/amazePushSource.spec.js: "ตรวจสอบ" ALWAYS posts dry_run:true
// first, and "ยืนยัน sync" (dry_run:false) is only reachable once a dry run has
// come back with at least one sendable SKU. A dry run never writes on Amaze and
// never uploads an image (§6B.4), so it is always safe to press; the confirm is
// the one that costs something, which is why it is the one that is gated.
//
// Everything decidable lives in @/utils/amazePush — this component only renders
// and sequences, because this repo's jest cannot mount a .vue file.
import { Select, Option, Table, TableColumn, Tag, Progress } from "element-ui";
import { AppCatalogProductService } from "@/resource";
import {
    AMAZE_PLATFORM_ID,
    PUSH_PLATFORMS,
    POLL_INTERVAL_MS,
    PUSH_STEP_LABEL_STEPS,
    isPushablePlatform,
    pushBody,
    payloadSummary,
    pushSkuRows,
    pushRejects,
    pushWarnings,
    rejectsFromError,
    rejectDisplayText,
    rejectDetailText,
    isProductLevelReject,
    warningDisplayText,
    skuActionTagType,
    skuActionI18nKey,
    skuRowReasonText,
    pushErrorI18nKey,
    pushErrorDetail,
    isCategoryNotMapped,
    isPayloadNotBuildable,
    pushInFlightJobId,
    pushStateI18nKey,
    pushStepI18nKey,
    pushStepIndex,
    pushJobProgressPercent,
    isPushInFlight,
    isPushFailed
} from "@/utils/amazePush";

export default {
    name: "amaze-push-modal",
    components: {
        // Element UI is NOT registered globally in this app (only Input,
        // Tooltip and Popover are — src/plugins/globalComponents.js), so every
        // el-* used above has to be registered here.
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Progress.name]: Progress
    },
    props: {
        show: { type: Boolean, default: false },
        productId: { type: [Number, String], default: 0 },
        productName: { type: String, default: "" }
    },
    data() {
        return {
            platformId: AMAZE_PLATFORM_ID,
            platforms: PUSH_PLATFORMS,
            stepLabels: PUSH_STEP_LABEL_STEPS,
            loading: false,
            polling: false,
            preview: null,
            rejects: [],
            errorKey: "",
            errorDetail: "",
            jobId: 0,
            status: null,
            // The live poll handle, so it can be cleared on close / destroy.
            // A modal that is dismissed mid-push must not keep hitting the API
            // for the rest of the session.
            pollTimer: null
        };
    },
    computed: {
        platformEnabled() {
            return isPushablePlatform(this.platformId);
        },
        summary() {
            return payloadSummary(this.preview);
        },
        actionKey() {
            const action = (this.preview && this.preview.action) || "";
            return action === "update" ? "amaze_push.action.update" : "amaze_push.action.create";
        },
        skus() {
            return pushSkuRows(this.preview);
        },
        warnings() {
            return pushWarnings(this.preview);
        },
        isDryRunPreview() {
            return !!(this.preview && this.preview.dry_run === true);
        },
        // A 422 carries rejects and NO payload: the list is the blocker.
        blockingRejects() {
            return this.preview ? [] : this.rejects;
        },
        // A 200 carries rejects ALONGSIDE a payload: those SKUs are skipped and
        // the rest still goes (§6B.3 rule 7).
        skippedRejects() {
            return this.preview ? this.rejects : [];
        },
        // Confirm stays dead until a dry run has returned a preview with at
        // least one SKU marked `send`.
        //
        // PER-SKU REJECTS DO NOT DISABLE IT. The handler answers 200 with a
        // buildable payload and the rejects of the SKUs it is leaving out, on
        // purpose; requiring an empty list made a product with one bad SKU code
        // unpushable from this screen, with no way out but editing a SKU the
        // operator did not want to send. What must stay impossible is a push
        // with nothing to send — hence the `send` check, which is also why the
        // 422 no_active_sku round trip is unreachable from this button.
        canConfirm() {
            if (!this.platformEnabled) return false;
            if (!this.preview) return false;
            return this.skus.some((s) => s.action === "send");
        },
        state() {
            return (this.status && this.status.state) || "";
        },
        stateKey() {
            return pushStateI18nKey(this.state);
        },
        // A terminal state — the result block replaces the buttons.
        finishedState() {
            const s = this.state;
            return s !== "" && !isPushInFlight(s);
        },
        isFailed() {
            return isPushFailed(this.state) || this.state === "rejected";
        },
        platformProductId() {
            return (this.status && this.status.platform_product_id) || "";
        },
        stateDetail() {
            return (this.status && this.status.state_detail) || "";
        },
        lastError() {
            return (this.status && this.status.last_error) || "";
        },
        job() {
            return (this.status && this.status.job) || null;
        },
        stepKey() {
            return pushStepI18nKey(this.job && this.job.step);
        },
        stepIndex() {
            return pushStepIndex(this.job && this.job.step);
        },
        progressPercent() {
            return pushJobProgressPercent(this.job);
        },
        showMappingLink() {
            return this.errorKey === "amaze_push.error.category_not_mapped";
        }
    },
    watch: {
        show(val) {
            if (val) this.reset();
            else this.stopPolling();
        }
    },
    beforeDestroy() {
        this.stopPolling();
    },
    methods: {
        rejectText(reject) {
            return rejectDisplayText(reject, this.$t.bind(this));
        },
        rejectDetail: rejectDetailText,
        isProductLevel: isProductLevelReject,
        warningText(warning) {
            return warningDisplayText(warning, this.$t.bind(this));
        },
        actionTagType: skuActionTagType,
        actionI18nKey: skuActionI18nKey,
        skuReason(row) {
            return skuRowReasonText(row, this.$t.bind(this));
        },
        reset() {
            this.platformId = AMAZE_PLATFORM_ID;
            this.loading = false;
            this.preview = null;
            this.rejects = [];
            this.errorKey = "";
            this.errorDetail = "";
            this.jobId = 0;
            this.status = null;
            this.stopPolling();
        },
        handleShowChange(val) {
            this.$emit("update:show", val);
        },
        close() {
            this.stopPolling();
            this.$emit("update:show", false);
        },
        clearMessages() {
            this.rejects = [];
            this.errorKey = "";
            this.errorDetail = "";
        },
        // ตรวจสอบ = dry run. Never writes on Amaze, never uploads an image.
        handleCheck() {
            return this.send(true);
        },
        // ยืนยัน sync = the real push. Guarded by canConfirm so it can never be
        // the first call.
        handleConfirm() {
            if (!this.canConfirm) return Promise.resolve();
            return this.send(false);
        },
        // ลองใหม่ after a failure: clear the terminal state and preview again,
        // which puts the operator back at step 2 rather than firing a blind
        // second write.
        retry() {
            this.status = null;
            this.jobId = 0;
            this.preview = null;
            this.clearMessages();
            return this.handleCheck();
        },
        send(dryRun) {
            const self = this;
            this.clearMessages();
            this.loading = true;
            return AppCatalogProductService.push(this.productId, this.platformId, pushBody(dryRun, {}))
                .then(function(resp) {
                    self.loading = false;
                    const data = (resp && resp.data) || {};
                    self.preview = data;
                    // A 200 may still carry per-SKU rejects alongside the SKUs
                    // that did pass; show them without treating the call as a
                    // failure.
                    self.rejects = pushRejects(data);
                    if (dryRun === false) {
                        self.jobId = Number(data.job_id) || 0;
                        self.startPolling();
                    }
                    return resp;
                })
                .catch(function(err) {
                    self.loading = false;
                    self.preview = null;
                    const rejects = rejectsFromError(err);
                    // A 422 whose only content is the reject list renders as the
                    // list — a headline above it would only repeat it. A
                    // payload_not_buildable 422 carries rejects TOO, but the
                    // reason the push is impossible is in its `message`, so that
                    // one keeps its own sentence and prints the message under it.
                    if (rejects.length && !isPayloadNotBuildable(err)) {
                        self.rejects = rejects;
                        return;
                    }
                    self.rejects = rejects;
                    self.errorKey = pushErrorI18nKey(err);
                    self.errorDetail = pushErrorDetail(err);
                    if (isCategoryNotMapped(err)) return;
                    // A push already in flight is not a dead end: re-attach to
                    // the running job instead of asking the operator to guess
                    // whether their click did anything.
                    const runningJob = pushInFlightJobId(err);
                    if (runningJob > 0) {
                        self.jobId = runningJob;
                        self.startPolling();
                    }
                });
        },
        // Poll every 2s (spec §6B.8 step 3) until the state leaves `pushing`.
        startPolling() {
            this.stopPolling();
            this.polling = true;
            this.fetchStatus();
            this.pollTimer = setInterval(this.fetchStatus, POLL_INTERVAL_MS);
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
            this.polling = false;
        },
        fetchStatus() {
            const self = this;
            return AppCatalogProductService.pushStatus(this.productId, this.platformId)
                .then(function(resp) {
                    const data = (resp && resp.data) || {};
                    self.status = data;
                    if (!isPushInFlight(data.state)) {
                        self.stopPolling();
                        self.$emit("pushed", {
                            product_id: self.productId,
                            platform_id: self.platformId,
                            state: data.state || ""
                        });
                    }
                })
                .catch(function(err) {
                    // A failing STATUS poll must not be mistaken for a failed
                    // push: stop polling, say why, and leave the job id on
                    // screen so the operator can reopen and look again.
                    self.stopPolling();
                    self.errorKey = pushErrorI18nKey(err);
                    self.errorDetail = pushErrorDetail(err);
                });
        },
        openCategoryMap() {
            this.$router.push("/seller/platform-category-map");
        }
    }
};
</script>

<style>
.amaze-push-modal .modal-body {
    max-height: 70vh;
    overflow-y: auto;
}
</style>
